/**
 * ticket-inapp.palette — the grounds the package's in-app card variants
 * paint, pinned to the `TICKET_GROUNDS` values (ticket.palette.ts), as the
 * one `<style>` block every variant mounts.
 *
 * A TICKET IS A DOCUMENT, SO ITS PALETTE IS ITS OWN. Editorial and strata
 * compose from exactly two tones — a near-black ground carrying the dense
 * facts, a warm near-white ground carrying the identity and the credential —
 * and which one is which is the composition, not a preference the viewer's
 * surface gets a vote in. Stencil composes from one solid vivid-orange
 * ground under white ink instead — its own single-tone identity, pinned the
 * same way. Theme tokens cannot express any of this: `surface-containerLowest`
 * is neutral tone 4 under a dark scheme and tone 100 under a light one, and
 * `surface-inverse` is tone 90 and tone 20 (theme.format.ts's `isDark ?`
 * ladder) — so a card built from those families reads as designed under one
 * scheme and exactly inverted under the other, which on editorial/strata puts
 * the QR's `#000000` modules (ticket-inapp.qr-hero.ts) onto a near-black
 * ground at ~1.6:1. The credential stops being scannable, and the buyer's
 * screen stops matching the file they were emailed.
 *
 * The values — `TICKET_GROUNDS` (ticket.palette.ts), interpolated into the
 * block below — ARE the tones each composition was proven against, frozen so
 * the same card renders on a light host, a dark host, the rasterized share
 * PNG and the emailed PDF alike. The package already holds one document
 * palette on this reasoning (ticket-email.template.ts's `INK`/`PAPER`); this
 * is the in-app set. Editorial/strata's pair is warm rather than achromatic
 * because the neutral palette they came from carries chroma 4 — the warmth is
 * the design's, and pinning it makes it a constant rather than a function of
 * whichever brand seed the host theme happens to run.
 *
 * WHAT STAYS ON THE THEME: the hairline around a card's OUTER edge. That
 * border separates the card from the page it sits on, so it is the one edge
 * whose job depends on the host — `outlineVariant` tracks the host surface
 * and reads correctly against a dark or a light page, which a pinned value
 * could not.
 *
 * Shipped as co-located CSS rather than Tailwind utilities for the reason
 * every stylesheet in this package is (ticket-inapp.editorial.template.ts):
 * an arbitrary utility used only HERE is never emitted into a consumer app's
 * Tailwind output, so it would render inert in the host that mounts the card.
 *
 * Every class carries `background-color` and `color` outright rather than
 * re-pointing a component's own tint token: a declaration in the outer tree
 * beats a shadow tree's `:host` rule for the same element (CSS Scoping §3.3),
 * so these win wherever they are placed without reaching for a library-private
 * custom property that could be renamed out from under the credential.
 * `--schmancy-sys-color-outlineVariant` IS re-pointed on BOTH grounds —
 * that one is the public token every schmancy rule inside it resolves through
 * (`<schmancy-divider>`'s `border-outlineVariant`), so pinning it once here
 * reaches them all.
 */
import { html, type TemplateResult } from "lit";
import { TICKET_GROUNDS } from "./ticket.palette.ts";

export const ticketCardGrounds: TemplateResult = html`
  <style>
    .tkt-ink-ground {
      background-color: ${TICKET_GROUNDS.ink.background};
      color: ${TICKET_GROUNDS.ink.ink};
      border-color: transparent;
      /* The ink ground's own hairline tone, sibling to the paper ground's
         below and pinned for the same reason: a rule drawn inside a pinned
         ground must hold its tone with that ground, and the host's
         outlineVariant is a light grey under a light scheme — a bright line
         across a near-black card. In-app-only, so it stays a literal here
         rather than joining TICKET_GROUNDS. */
      --schmancy-sys-color-outlineVariant: #3a332c;
    }
    .tkt-paper-ground {
      background-color: ${TICKET_GROUNDS.paper.background};
      color: ${TICKET_GROUNDS.paper.ink};
      /* In-app-only: the darkened divider tone the paper ground pins for the
         schmancy rules inside it — a ground for nothing, so it stays a
         literal here rather than joining TICKET_GROUNDS. */
      --schmancy-sys-color-outlineVariant: #2d241a;
    }
    .tkt-stencil-ground {
      background-color: ${TICKET_GROUNDS.stencil.background};
      color: ${TICKET_GROUNDS.stencil.ink};
      border-color: transparent;
    }
  </style>
`;
